import React from 'react'
import { format } from 'date-fns'
import { parseDateOnly } from '../utils/format'

/**
 * The "Repeats" control for the event form.
 *
 * Extracted from Calendar.js on 2026-09-02.  Feedback #819 was "I can not
 * schedule repeat event", and the cause was not the recurrence engine — it was
 * that this control existed ONLY on the calendar page.  An attorney adding an
 * event from inside a matter had no repeat option at all, and matter-linked is
 * how most events actually get created.
 *
 * It lives in one place deliberately.  Bug #820 was a dropdown that had drifted
 * between Calendar.js and MatterDetail.js, and copying 120 lines of form into a
 * second file is exactly how that happens again.
 *
 * The caller owns whether to render it: recurrence is create-only, because
 * changing the pattern on an existing series needs a delete-and-recreate flow
 * that does not exist yet.
 */

const BYDAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA']

/** Which weekday the seed event falls on, as a BYDAY code. */
export function seedWeekdayOf (form) {
  if (!form?.start_time) return null
  const d = parseDateOnly(String(form.start_time).slice(0, 10))
  return Number.isNaN(d.getTime()) ? null : BYDAY_CODES[d.getDay()]
}

/**
 * A weekly series whose day list excludes the seed event's own weekday is
 * rejected by the server with a 400.  Nothing in the UI used to say so.
 */
export function bydayExcludesSeedOf (form) {
  if (form?.recurrence_frequency !== 'weekly') return false
  const picked = String(form.recurrence_byday || '').split(',').filter(Boolean)
  if (picked.length === 0) return false          // empty = "use the seed's day"
  const seed = seedWeekdayOf(form)
  return !!seed && !picked.includes(seed)
}

/** The recurrence fields a fresh event form should start with. */
export const emptyRecurrence = {
  recurrence_frequency: '',
  recurrence_interval: 1,
  recurrence_byday: '',
  recurrence_end_type: 'never',
  recurrence_until: '',
  recurrence_count: '',
}

/**
 * Translate the form's UI-only recurrence_end_type into the parameters the API
 * accepts (until XOR count), and drop the fields the server does not take.
 * Mutates and returns the payload so both callers behave identically.
 */
export function applyRecurrenceToPayload (payload) {
  if (payload.recurrence_frequency) {
    if (payload.recurrence_end_type === 'count') {
      delete payload.recurrence_until
    } else if (payload.recurrence_end_type === 'until') {
      delete payload.recurrence_count
    } else {
      // 'never' -> bounded by the server's MAX_OCCURRENCES cap
      delete payload.recurrence_count
      delete payload.recurrence_until
    }
  } else {
    delete payload.recurrence_frequency
    delete payload.recurrence_interval
    delete payload.recurrence_byday
    delete payload.recurrence_until
    delete payload.recurrence_count
  }
  delete payload.recurrence_end_type   // UI-only, not accepted by the API
  return payload
}

export default function RecurrenceFields ({ form, setForm }) {
  const seedWeekday = () => seedWeekdayOf(form)
  const bydayExcludesSeed = () => bydayExcludesSeedOf(form)

  return (

    <details className="md:col-span-2 border border-gray-200 rounded-lg overflow-hidden">
    <summary className="cursor-pointer px-4 py-3 bg-gray-50 hover:bg-gray-100 text-sm font-medium text-gray-700 select-none">
      🔁 Repeats — {form.recurrence_frequency
        ? `${form.recurrence_frequency}${form.recurrence_interval > 1 ? ` × ${form.recurrence_interval}` : ''}`
        : 'One time only'}
    </summary>
    <div className="p-4 space-y-3 bg-white">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Repeat frequency</label>
        <select value={form.recurrence_frequency}
          onChange={e => {
            const freq = e.target.value
            // Start weekly in a VALID state: pre-select the seed
            // event's own weekday rather than an empty list the
            // user can accidentally make illegal.
            const seed = seedWeekday()
            setForm({
              ...form,
              recurrence_frequency: freq,
              recurrence_byday: freq === 'weekly' && !form.recurrence_byday && seed ? seed : form.recurrence_byday,
            })
          }}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
          <option value="">One time only (no repeat)</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
        </select>
      </div>
      {form.recurrence_frequency && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Every</label>
            <div className="flex items-center gap-2">
              <input type="number" min={1} max={100}
                value={form.recurrence_interval}
                onChange={e => setForm({ ...form, recurrence_interval: e.target.value })}
                onBlur={e => setForm({ ...form, recurrence_interval: Math.max(1, Math.min(100, parseInt(e.target.value) || 1)) })}
                className="w-20 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <span className="text-sm text-gray-600">
                {form.recurrence_frequency === 'daily' ? 'day(s)'
                 : form.recurrence_frequency === 'weekly' ? 'week(s)'
                 : form.recurrence_frequency === 'monthly' ? 'month(s)'
                 : 'year(s)'}
              </span>
            </div>
          </div>
          {form.recurrence_frequency === 'weekly' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">On which days</label>
              <div className="flex flex-wrap gap-2">
                {[['MO', 'Mon'], ['TU', 'Tue'], ['WE', 'Wed'], ['TH', 'Thu'], ['FR', 'Fri'], ['SA', 'Sat'], ['SU', 'Sun']].map(([code, label]) => {
                  const days = (form.recurrence_byday || '').split(',').filter(Boolean)
                  const active = days.includes(code)
                  return (
                    <button type="button" key={code}
                      onClick={() => {
                        const next = active ? days.filter(d => d !== code) : [...days, code]
                        setForm({ ...form, recurrence_byday: next.join(',') })
                      }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium ${active ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      {label}
                    </button>
                  )
                })}
              </div>
              <p className="text-xs text-gray-500 mt-1">Leave empty to use only the seed event's weekday.</p>
              {bydayExcludesSeed() && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 mt-1.5">
                  This event is on a {format(parseDateOnly(form.start_time.slice(0, 10)), 'EEEE')}, but that day isn't selected.
                  Add it, or move the event to one of the days you picked — otherwise the series can't be created.
                </p>
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ends</label>
            <div className="space-y-2">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="radio" value="never"
                  checked={form.recurrence_end_type === 'never'}
                  onChange={e => setForm({ ...form, recurrence_end_type: e.target.value })} />
                <span className="text-sm">Never (capped at 730 occurrences for safety)</span>
              </label>
              <div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="radio" value="count"
                    checked={form.recurrence_end_type === 'count'}
                    onChange={e => setForm({ ...form, recurrence_end_type: e.target.value })} />
                  <span className="text-sm">After</span>
                  <input type="number" min={2} max={730}
                    value={form.recurrence_count}
                    onChange={e => setForm({ ...form, recurrence_count: e.target.value })}
                    onBlur={e => setForm({ ...form, recurrence_count: Math.max(2, Math.min(730, parseInt(e.target.value) || 10)) })}
                    disabled={form.recurrence_end_type !== 'count'}
                    className="w-20 border border-gray-300 rounded-lg px-2 py-1 text-sm disabled:opacity-40" />
                  <span className="text-sm">occurrences (including this one)</span>
                </label>
              </div>
              <div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input type="radio" value="until"
                    checked={form.recurrence_end_type === 'until'}
                    onChange={e => setForm({ ...form, recurrence_end_type: e.target.value })} />
                  <span className="text-sm">On</span>
                  <input type="date"
                    value={form.recurrence_until}
                    onChange={e => setForm({ ...form, recurrence_until: e.target.value })}
                    disabled={form.recurrence_end_type !== 'until'}
                    className="border border-gray-300 rounded-lg px-2 py-1 text-sm disabled:opacity-40" />
                </label>
              </div>
            </div>
          </div>
          {form.sync_to_google && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              ⚠ Google Calendar sync will push each occurrence as a separate event (not a series).
              Editing recurrence in Google won't reflect back here.
            </div>
          )}
        </>
      )}
    </div>
    </details>
  )
}
